import { GetServerSideProps, NextPage } from 'next'
import { GlobalRequest } from '~/components/global-requset'
import {
  GET_BANNER_LIST,
  GET_ITEM_LIST_BY_CATEGORY,
  GET_MALL_CATEGORY_LIST,
  GET_SPECIAL_LIST,
} from '~/graphqls/gqls'
import {
  getBannerList,
  getBannerList_marketingModule_bannerList,
  getItemListByCategory,
  getItemListByCategoryVariables,
  getItemListByCategory_marketingModule_itemList,
  getMallCategoryList,
  getMallCategoryListVariables,
  getMallCategoryList_marketingModule_categoryList,
  getSpecialList,
  getSpecialListVariables,
  getSpecialList_marketingModule_specials_edges_node,
  MallProductStatus,
  MallCategoryType,
} from '~/graphqls/types'
import { getGraphData } from '~/utils'
import { CommonSection } from '~/components/business/CommonSection'
import { Col, Row } from 'antd'
import { SEOHead } from '~/components/business/SEOHead'
import { FeatureSection } from '~/components/pages/mall/FeatureSection'
import { Category } from '~/components/pages/mall/Category'
import { Container } from '~/components/ui/Container'
import { MallItem } from '~/components/business/MallItem'
import { Layout } from '~/layouts/default'
import { MyBtn } from '~/components/ui/MyBtn'
import Link from 'next/link'
import { SwapRightOutlined } from '@ant-design/icons'
import { SpecialModule } from '~/components/pages/mall/SpecialModule'
import { Fragment } from 'react'

const Mall: NextPage<{
  bannerData: getBannerList_marketingModule_bannerList[]
  itemListForCategory: { data: getItemListByCategory }[]
  baseCategoryData: getMallCategoryList_marketingModule_categoryList[]
  specialData: getSpecialList_marketingModule_specials_edges_node[]
}> = ({ bannerData, itemListForCategory, specialData, baseCategoryData }) => {
  return (
    <Layout>
      <SEOHead title="文创商城" />
      {!!bannerData.length && <FeatureSection data={bannerData} />}
      {!!baseCategoryData?.length && <Category data={baseCategoryData} />}
      {!!itemListForCategory?.length && (
        <section>
          {itemListForCategory?.map((item, index) => {
            const cateInfo = item.data.marketingModule
              ?.itemList as getItemListByCategory_marketingModule_itemList
            const itemList = getGraphData(
              item.data.marketingModule?.itemList?.itemList?.edges || []
            )

            const hasNextPage =
              item.data.marketingModule?.itemList?.itemList?.pageInfo
                ?.hasNextPage || false

            const isLast = index === itemListForCategory.length - 1

            return (
              <Fragment key={index}>
                <CommonSection
                  title={cateInfo?.name}
                  style={{
                    paddingBottom: isLast ? 40 : 8,
                    marginBottom: isLast ? -40 : 0,
                  }}
                  extra={
                    !isLast &&
                    hasNextPage && (
                      <Link href={`/mall/category?id=${cateInfo?.id}`}>
                        <a>
                          <MyBtn>
                            查看更多 <SwapRightOutlined />
                          </MyBtn>
                        </a>
                      </Link>
                    )
                  }
                >
                  <Container>
                    <Row gutter={32}>
                      {itemList?.map(subitem => (
                        <Col span={8} key={subitem.id}>
                          <MallItem data={subitem} />
                        </Col>
                      ))}
                    </Row>
                    {isLast && hasNextPage && (
                      <Row gutter={32} style={{ margin: '32px auto' }}>
                        <Col span={8} offset={8}>
                          <Link href={`/mall/category?id=${cateInfo?.id}`}>
                            <a>
                              <MyBtn size="large" type="primary" block>
                                查看更多
                              </MyBtn>
                            </a>
                          </Link>
                        </Col>
                      </Row>
                    )}
                  </Container>
                </CommonSection>
                {index === 0 && specialData?.length ? (
                  <CommonSection
                    title="专题推荐"
                    style={{
                      paddingBottom: 8,
                      marginBottom: 0,
                    }}
                  >
                    <Container>
                      <SpecialModule data={specialData} />
                    </Container>
                  </CommonSection>
                ) : (
                  ''
                )}
              </Fragment>
            )
          })}
        </section>
      )}
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps = async context => {
  const { initialState, client } = await GlobalRequest(context)
  const [bannerRes, categoryRes, baseCategoryRes, specialRes] =
    await Promise.all([
      client.query<getBannerList>({
        query: GET_BANNER_LIST,
        fetchPolicy: 'no-cache',
      }),
      client.query<getMallCategoryList, getMallCategoryListVariables>({
        query: GET_MALL_CATEGORY_LIST,
        variables: {
          type: MallCategoryType.MARKET_CATEGORY,
        },
        fetchPolicy: 'no-cache',
      }),
      client
        .query<getMallCategoryList, getMallCategoryListVariables>({
          query: GET_MALL_CATEGORY_LIST,
          variables: {
            type: MallCategoryType.BASE_CATEGORY,
          },
          fetchPolicy: 'no-cache',
        })
        .catch(() => {}),
      client
        .query<getSpecialList, getSpecialListVariables>({
          query: GET_SPECIAL_LIST,
          variables: {
            param: {
              code: 2,
              status: MallProductStatus.ON,
            },
          },
          fetchPolicy: 'no-cache',
        })
        .catch(() => {}),
    ])

  const categoryList = categoryRes.data.marketingModule?.categoryList || []

  const promises = categoryList?.map((item, index) => {
    return client.query<getItemListByCategory, getItemListByCategoryVariables>({
      query: GET_ITEM_LIST_BY_CATEGORY,
      variables: {
        id: item?.id,
        offset: 0,
        first: index === categoryList?.length - 1 ? 12 : 3,
      },
      fetchPolicy: 'no-cache',
    })
  })

  const itemListForCategoryRes = await Promise.all(promises)

  return {
    props: {
      initialState,
      baseCategoryData:
        baseCategoryRes?.data.marketingModule?.categoryList?.slice(0, 8) || [],
      bannerData: bannerRes?.data?.marketingModule?.bannerList || [],
      itemListForCategory: itemListForCategoryRes || [],
      specialData: getGraphData(
        specialRes?.data?.marketingModule?.specials?.edges || []
      )?.filter(it => it.style === 2),
    },
  }
}

export default Mall
